import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useIsIOS } from "@/hooks/useIsIOS";
import { useNotification } from "@/hooks/useNotification";
import { useUser } from "@/hooks/useUser";
import { useConfetti } from "@/providers/confettiProvider";
import type { IOSProduct } from "@/lib/types";
import { proDialog } from "@/state/dialogs";
import { IOS_PRICING } from "@/config/pricing";
import { purchaseSubscriptionIos } from "@/lib/ios/purchase-subscription-ios";
import { subscription } from "@/lib/auth-client";
import { PATHS } from "@/config/paths";
import { selectedPlanAtom } from "@/state/payment";

type IAPContextType = {
  IOSproducts: IOSProduct[];
  purchaseIOSProduct: (offerName: string) => void;
};

const IAPContext = createContext<IAPContextType | null>(null);

// IOS: IAPProvider
export function IAPProvider({ children }: { children: React.ReactNode }) {
  const isIOS = useIsIOS();
  const { userId, user, refetchUser } = useUser();
  const [IOSproducts, setIOSproducts] = useState<IOSProduct[]>([]);
  const selectedPlan = useAtomValue(selectedPlanAtom);
  const { showNotification } = useNotification();
  const { fireConfetti } = useConfetti();
  const setProDialogOpen = useSetAtom(proDialog);

  const requestProducts = useCallback(() => {
    try {
      window.nativeBridge?.requestProducts([
        IOS_PRICING.monthly_subscription,
        IOS_PRICING.yearly_subscription,
      ]);
    } catch (error) {
      console.error("Error requesting products:", error);
      showNotification({
        type: "error",
        message: "Products Request Failed",
        description: "Error requesting products",
      });
    }
  }, [showNotification]);

  useEffect(() => {
    if (!isIOS) {
      return;
    }

    const abortController = new AbortController();

    // Check if iOS IAP is available
    if (window.nativeBridge?.requestProducts) {
      // Only request products if we haven't already
      if (IOSproducts.length === 0) {
        requestProducts();
      }
      // Check subscription status on mount
      window.nativeBridge.checkSubscriptionStatus();
    }

    const handleProducts = (event: CustomEvent) => {
      if (event.detail) {
        try {
          setIOSproducts(JSON.parse(event.detail));
        } catch (error) {
          console.error("Error parsing products:", error);
          showNotification({
            type: "error",
            message: "Failed to load products",
          });
        }
      }
    };

    const handlePurchaseResult = async (event: CustomEvent) => {
      if (event.detail) {
        const result = event.detail;
        if (result === "success" && userId) {
          try {
            // Call the server function directly
            await purchaseSubscriptionIos({
              data: {
                userId,
                result, // Assuming 'result' has the structure expected by the Zod schema
                selectedPlan,
              },
            }).then(() => {
              refetchUser();
              fireConfetti({ golden: true });
              setProDialogOpen(false);

              if (window.umami) {
                window.umami.track("pro-subscription-success");
              }
            });
          } catch (error) {
            console.error("Error updating subscription status:", error);
            showNotification({
              type: "error",
              message: "Subscription Update Failed",
              description: "Please contact support if this persists.",
            });
          }
        } else if (result === "cancelled") {
          // Handle cancellation if needed
          showNotification({
            type: "info",
            message: "Purchase Cancelled",
          });
        }
      }
    };

    // IOS: Remove subscription if no longer active
    const handleSubscriptionStatus = async (event: CustomEvent) => {
      const status = event.detail;
      // Use cancelSubscription directly
      if (status !== "active" && user?.subscriptionProvider === "ios" && userId) {
        await subscription
          .cancel({
            referenceId: userId,
            returnUrl: import.meta.env.VITE_BASE_URL + PATHS.DASHBOARD,
          })
          .catch(() => {
            showNotification({
              type: "error",
              message: "Failed to Update Subscription Status",
              description:
                "Could not update your subscription status locally after it changed on iOS.",
            });
          });
      }
    };

    window.addEventListener("iap-products-result", handleProducts as EventListener, {
      signal: abortController.signal,
    });

    window.addEventListener(
      "iap-purchase-result",
      (event: Event) => handlePurchaseResult(event as CustomEvent),
      {
        signal: abortController.signal,
      },
    );

    window.addEventListener(
      "iap-subscription-status",
      (event: Event) => handleSubscriptionStatus(event as CustomEvent),
      {
        signal: abortController.signal,
      },
    );

    return () => {
      abortController.abort();
    };
  }, [
    isIOS,
    user?.subscriptionProvider,
    fireConfetti,
    requestProducts,
    showNotification,
    userId,
    setProDialogOpen,
    IOSproducts.length,
    selectedPlan,
    refetchUser,
  ]);

  const purchaseIOSProduct = (offerName: string) => {
    try {
      window.nativeBridge?.requestPurchase(offerName);
    } catch (error) {
      console.error("Error sending purchase request:", error);
      showNotification({
        type: "error",
        message: "Purchase Failed",
        description: "Error sending purchase request",
      });
    }
  };

  return (
    <IAPContext.Provider
      value={{
        IOSproducts,
        purchaseIOSProduct,
      }}
    >
      {children}
    </IAPContext.Provider>
  );
}

export const useIAP = () => {
  const context = useContext(IAPContext);

  if (!context) {
    throw new Error("useIAP must be used within an IAPProvider");
  }

  return context;
};
